import {
  ConciergeHeaderWidget as ConciergeWidgetHeader,
  useWidgetConfig,
  useResolveColor,
  customColor,
  useStudioAgentName,
  useThemeColors,
} from '@brandclub/bc-widgets';
import type { ConciergePanelWidgetState } from '@brandclub/bc-widgets';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast, Toaster } from 'sonner';
import { useBrandAppConfig } from '../api/cache/appConfigQueryOptions';
import { useAppBootstrapV2 } from '../api/cache/appContextV2QueryOptions';
import { getHeadersWithUniqueVisitorId, getUserSignedInState, AmplifyHub } from '../Auth';
// Note: CSS is NOT imported here to avoid build conflicts with apps that don't use Tailwind v4
// Consumers should import concierge-tailwind.css directly in their app entry point
import { productApi } from './productApi';
import { ConciergeChatAreaV2 } from './components/ConciergeChatAreaV2';
import { IconCompose } from './components/ConciergeIcons';
import DevOptionsPanel from './components/DevOptionsPanel';
import { useDevOptions } from './hooks/useDevOptions';
import { PinnedProductContext } from './components/PinnedProductContext';
import { useConciergeContext } from './ConciergeContextProvider';
import { useMerchantCartOrigin } from './hooks/useMerchantCartOrigin';
import { sameOriginConciergeBase } from './conciergePageUrl';
import { useAutoScroll } from './hooks/useAutoScroll';
import { useConciergeTracking } from './hooks/useConciergeTracking';
import { useConversationLoader } from './hooks/useConversationLoader';
import { usePageContext } from './hooks/usePageContext';
import { useConciergeChat } from './hooks/useConciergeChat';
import { useConciergeAgreementGate } from './hooks/useConciergeAgreementGate';
import { ConciergeAgreementCard } from './components/ConciergeAgreementCard';
import { VoiceCallOverlay } from './voice/VoiceCallOverlay';
import { ProductDetailPanel } from './components/ProductDetailPanel';
import { AnalyticsService, AnalyticsTrackingEvent } from '../analytics';
import { useConciergePendingQuery } from './stores/useConciergePendingQuery';
import { ConversationAccessPointType } from './tracking';
import { SectionBasedProduct } from './types';
import { ConciergeWidgetState, PageContext } from './types/widget';

interface ConciergeWidgetChatContentProps {
  pageContext?: PageContext;
  widgetState: ConciergeWidgetState;
  showMaximize?: boolean;
  isMaximized?: boolean;
  onMaximize?: () => void;
  onMinimize?: () => void;
  /** Callback when a variant is selected in a FeaturedProductWidget - triggers navigation to that variant's PDP */
  onVariantSelect?: (stacklineSku: string) => void;
  title?: string;
  /** Initial query to auto-send when widget opens (passed via props to avoid cross-bundle Zustand issues) */
  initialQuery?: string;
  /** Stackline SKU associated with the initial query (for product context) */
  initialQueryStacklineSku?: string;
  /** Called after the initial query has been processed/sent */
  onInitialQueryProcessed?: () => void;
  /** Brand ID for fetching suggested queries. If not provided, will try to get from URL params or brand config */
  brandId?: number;
  /** Explicit override for the dedicated concierge page URL. If not provided, uses the
   *  server-computed `canonicalConciergeUrl` from the brand-app config (the brand's chosen
   *  serving surface — Plan 6), falling back to origin + /concierge. */
  conciergePageUrl?: string;
}



const ConciergeWidgetChatContent: React.FC<ConciergeWidgetChatContentProps> = ({
  pageContext: providedPageContext,
  widgetState,
  showMaximize = true,
  isMaximized = false,
  onMaximize: _onMaximize,
  onMinimize,
  onVariantSelect,
  title,
  initialQuery,
  initialQueryStacklineSku,
  onInitialQueryProcessed,
  brandId: propBrandId,
  conciergePageUrl: propConciergePageUrl,
}) => {
  // Get conversation state from context (includes apiBaseUrl for checkout-shop-app)
  const conversationState = useConciergeContext();

  // Avoid "Conversation not found" toast when panel opens with a stale conversationId in URL.
  const initialConversationIdRef = useRef(conversationState.conversationId);
  const initialSharedConversationIdRef = useRef(conversationState.sharedConversationId);

  // Brand config
  const { data: config } = useBrandAppConfig(conversationState.apiBaseUrl);
  const { data: appBootstrapV2 } = useAppBootstrapV2(conversationState.apiBaseUrl);
  useMerchantCartOrigin(appBootstrapV2?.app?.channels);
  const studioAgentName = useStudioAgentName();
  const { themeColors } = useThemeColors();

  const params = new URLSearchParams(window.location.search);
  // Priority: prop > URL param > brand config
  const brandId = propBrandId?.toString() || params.get('brandId') || config?.brandIds?.[0]?.toString() || null;
  const appId = params.get('appId') || appBootstrapV2?.app.appId || null;
  // Input state
  const [searchInputValue, setSearchInputValue] = useState('');

  // Dev options - enabled via ?isDev=true URL param
  const devOpts = useDevOptions();

  // The signed-in shopper's first name, for the personalised greeting.
  //
  // Under the `shopify` auth type there is no Cognito session at all (D-42.2),
  // so asking Amplify returns "signed out" for a shopper who is very much
  // signed in — and the greeting fell back to "Hi there" for someone whose name
  // the sidebar was displaying. The store session's name arrives in the
  // skeleton-injected bootstrap, so prefer that when this app uses it.
  const shopifyFirstName =
    appBootstrapV2?.auth?.authType === 'shopify'
      ? ((window.__BC_BOOTSTRAP__?.customerFirstName as string | null | undefined) ?? undefined)
      : undefined;
  const [cognitoFirstName, setCognitoFirstName] = useState<string | undefined>(undefined);
  const userFirstName = shopifyFirstName ?? cognitoFirstName;
  useEffect(() => {
    const refreshUserFirstName = () => {
      getUserSignedInState().then((state) => {
        if (state.signedIn && state.userInfo?.attributes?.given_name) {
          setCognitoFirstName(state.userInfo.attributes.given_name);
        } else {
          setCognitoFirstName(undefined);
        }
      });
    };
    refreshUserFirstName();
    const unsubscribe = AmplifyHub.listen('auth', ({ payload: { event } }) => {
      if (['signIn', 'signIn_Complete', 'signOut'].includes(event)) {
        refreshUserFirstName();
      }
    });
    return () => unsubscribe();
  }, []);

  // Build concierge page URL with optional brandId, conversationId, and
  // sharedConversationId. Precedence: the origin we are already on when it
  // serves the concierge (`sameOriginConciergeBase`, avoids a 301 through the
  // admin handle), then an explicit host prop, then the server-computed
  // canonical URL for the brand's serving surface (Plan 6 / D-23), then
  // origin + /concierge. Never invent a canonical URL for a surface we are NOT
  // on — that is what the server-computed value is for.
  const getConciergePageUrl = useCallback(
    (opts: { conversationId?: string | null; sharedConversationId?: string | null } = {}) => {
      const base =
        sameOriginConciergeBase(appBootstrapV2?.app?.servingSurface) ??
        propConciergePageUrl ??
        config?.canonicalConciergeUrl ??
        `${window.location.origin}/concierge`;
      const url = new URL(base);
      if (brandId) url.searchParams.set('brandId', brandId);
      if (opts.conversationId) url.searchParams.set('conversationId', opts.conversationId);
      if (opts.sharedConversationId) url.searchParams.set('sharedConversationId', opts.sharedConversationId);
      return url.toString();
    },
    [propConciergePageUrl, config?.canonicalConciergeUrl, brandId, appBootstrapV2?.app?.servingSurface],
  );

  // Handle history icon click: open dedicated concierge page in a new window
  const handleHistoryClick = useCallback(() => {
    window.open(getConciergePageUrl(), '_blank', 'noopener,noreferrer');
  }, [getConciergePageUrl]);

  // Handle expand/maximize: open current conversation on concierge page in a new window (instead of expanding popup)
  const handleMaximizeClick = useCallback(() => {
    const url = getConciergePageUrl({
      conversationId: conversationState.conversationId,
      sharedConversationId: conversationState.sharedConversationId,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [getConciergePageUrl, conversationState.conversationId, conversationState.sharedConversationId]);

  // Ref to hold cancelStream so resetConversationState can use it without ordering issues
  const cancelStreamRef = useRef<() => void>(() => {});
  const resetChatMessagesRef = useRef<() => void>(() => {});

  const resetConversationState = useCallback(() => {
    // Cancel any in-flight stream before clearing state
    cancelStreamRef.current();
    resetChatMessagesRef.current();
    conversationState.setIsActivelyStreaming(false);
    conversationState.clearStreamingState();

    conversationState.setConversationId(null);
    conversationState.setSharedConversationId(null);
    conversationState.setConversationHistory([]);
    conversationState.setFollowUpQuestions([]);
    conversationState.setConversationPageContext(null);
    conversationState.setIsSharedView(false);
    conversationState.setSharedByUserName(undefined);
    conversationState.setSharedAtMessageIndex(undefined);
    conversationState.setSourceConversationId(undefined);
    conversationState.setSourceCheckpointIndex(undefined);
  }, [conversationState]);

  // Handle create new conversation: full reset so welcome view shows and user starts fresh.
  // The agreement gate is reset via ref so this callback can stay above the gate hook.
  const agreementResetRef = useRef<(() => void) | null>(null);
  const handleNewConversationClick = useCallback(() => {
    agreementResetRef.current?.();
    resetConversationState();
  }, [resetConversationState]);

  // "Start a new chat" from the voice error card. Product decision: the
  // secondary CTA always resets to a brand-new chat, regardless of how the call
  // started or whether it ever connected. To a shopper the overlay is invisible,
  // so "Reconnect" (resume) and "Start a new chat" (fresh) are the only two
  // mental models we expose.
  const handleVoiceCallClose = useCallback(
    (info?: { reason: string }) => {
      const dismissedToFreshChat = info?.reason === 'user_dismissed_error';
      console.info('[voice] widget call close landing decision', {
        reason: info?.reason,
        landing: dismissedToFreshChat ? 'fresh-chat' : 'stay-on-conversation',
      });
      if (dismissedToFreshChat) {
        agreementResetRef.current?.();
        resetConversationState();
      }
      conversationState.closeVoiceCall();
    },
    [conversationState, resetConversationState],
  );

  // Get page context - use provided one or fallback to hook
  // usePageContext will work even without react-router (uses window.location)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const hookPageContext = usePageContext();
  const pageContext = providedPageContext ?? hookPageContext;

  // Track previous page context to detect navigation and reset chat
  const previousPageContextRef = useRef<PageContext | null>(null);

  // Helper: are two page contexts meaningfully different? (same logic for "should reset")
  const isPageContextDifferent = useCallback((a: PageContext, b: PageContext) => {
    if (a.pageType !== b.pageType) return true;
    if (a.pageType === 'pdp' && b.pageType === 'pdp' && a.stacklineSku !== b.stacklineSku) return true;
    return false;
  }, []);

  // Inline error surfaced in the message list when the per-turn stream fails
  // or the LLM tripwire fires (replaces the prior toast surface).
  const [streamErrorMessage, setStreamErrorMessage] = useState<string | null>(null);
  useEffect(() => {
    setStreamErrorMessage(null);
  }, [conversationState.conversationId]);

  // Set the moment the user asks to stop, so the abort that follows is never
  // mistaken for a failure. Cleared by whichever runs first: the resulting
  // `onError`, or the next send.
  const userStoppedRef = useRef(false);

  // Handle stream errors - restore input and surface an inline error message
  const handleStreamError = useCallback((error: Error, originalMessage: string) => {
    setSearchInputValue(originalMessage);

    const msg = error?.message?.toLowerCase() ?? '';

    // An intentional stop is not an error. The AI SDK surfaces user aborts
    // through the same onError path, and showing "Something went wrong" after
    // the shopper taps stop reads as a crash (PetArmor test-log row 65).
    // Aborting BEFORE the first chunk yields a generic error with no
    // `AbortError` name and no "abort" in the message, so the error's shape
    // alone misses the fast-tap case; the ref records the user's intent.
    if (userStoppedRef.current || error?.name === 'AbortError' || msg.includes('abort')) {
      userStoppedRef.current = false;
      return;
    }

    let errorType: 'rate_limit' | 'bad_request' | 'network' | 'server' | 'unknown' = 'unknown';
    if (msg.includes('429') || msg.includes('rate limit')) errorType = 'rate_limit';
    else if (msg.includes('400') || msg.includes('bad request')) errorType = 'bad_request';
    else if (msg.includes('network') || msg.includes('failed to fetch')) errorType = 'network';
    else if (msg.includes('500') || msg.includes('server')) errorType = 'server';

    const errorMessages = {
      // Explicitly a pacing message — testers read the old copy as a possible
      // content refusal after adversarial prompts (test-log row 116).
      rate_limit: "I'm getting a lot of messages right now — nothing wrong with your question. Give it a few seconds and send it again.",
      bad_request: 'Please enter a message before sending.',
      network: 'Network error. Please check your connection and try again.',
      server: 'Something went wrong on our end. Please try again.',
      unknown: 'Something went wrong. Please try again.',
    };
    setStreamErrorMessage(errorMessages[errorType]);
  }, []);

  const temporaryChat =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('temporaryChat') === 'true';

  // AI SDK 6 chat hook — replaces useStreamingSearch
  const chat = useConciergeChat({
    apiBaseUrl: conversationState.apiBaseUrl,
    conversationId: conversationState.conversationId,
    sharedConversationId: conversationState.sharedConversationId,
    brandId,
    appId,
    devOptions: devOpts.devOptions,
    pdpStacklineSku: pageContext?.pageType === 'pdp' ? pageContext.stacklineSku : null,
    isOnChatWidget: true,
    temporaryChat,
    onError: (error) => handleStreamError(error, searchInputValue),
    onTripwire: (message) => {
      setStreamErrorMessage(message);
    },
    onConversationIdGenerated: (id) => {
      conversationState.setIsActivelyStreaming(true);
      conversationState.setConversationId(id);
      conversationState.setSharedConversationId(null);
      conversationState.setIsSharedView(false);
      conversationState.setSharedAtMessageIndex(undefined);
    },
  });

  // Prefer live chat.messages when available; fall back to DB-loaded conversationHistory
  const effectiveMessages = chat.messages.length > 0 ? chat.messages : conversationState.conversationHistory;

  // Remember where the current conversation was started (so we can reset when opening on a different page)
  useEffect(() => {
    const hasConversation = conversationState.conversationId != null || effectiveMessages.length > 0;
    if (hasConversation && conversationState.conversationPageContext == null) {
      conversationState.setConversationPageContext(pageContext);
    }
  }, [
    pageContext,
    conversationState.conversationId,
    effectiveMessages.length,
    conversationState.conversationPageContext,
    conversationState.setConversationPageContext,
  ]);

  // Reset conversation when page context changes (e.g., navigating between products)
  // Also reset on initial mount when opening the widget on a different page than where the conversation was started
  useEffect(() => {
    const previousContext = previousPageContextRef.current;
    const storedConversationContext = conversationState.conversationPageContext;

    if (previousContext === null) {
      // Initial mount (e.g. user closed widget, navigated, then opened again)
      previousPageContextRef.current = pageContext;
      const hasConversation = conversationState.conversationId != null || effectiveMessages.length > 0;
      if (
        hasConversation &&
        storedConversationContext != null &&
        isPageContextDifferent(pageContext, storedConversationContext)
      ) {
        resetConversationState();
      }
      return;
    }

    // Detect if the page context has meaningfully changed while widget was open
    const hasPageContextChanged = isPageContextDifferent(previousContext, pageContext);

    if (hasPageContextChanged) {
      resetConversationState();
    }

    // Update the previous context ref
    previousPageContextRef.current = pageContext;
  }, [pageContext, conversationState, isPageContextDifferent, resetConversationState]);

  // Pending access point ref - stores the access point for the next message
  // This allows us to track where a conversation was started from
  const pendingAccessPointRef = useRef<{
    accessPoint: ConversationAccessPointType;
    query: string;
  } | null>(null);

  // Tracks which conversationId has already had its access point recorded,
  // so subsequent messages in the same conversation don't fire duplicate events.
  const trackedConversationIdRef = useRef<string | null>(null);

  // Track if this is a new conversation (no existing messages)
  const isNewConversationRef = useRef<boolean>(true);
  useEffect(() => {
    isNewConversationRef.current = effectiveMessages.length === 0;
  }, [effectiveMessages.length]);

  // Initialize tracking hook
  const { trackConversationStart, trackPageContext } = useConciergeTracking({
    conversationId: conversationState.conversationId,
    pageContext,
    appId,
  });

  // Track when a new conversationId is received (conversation started)
  const previousConversationIdRef = useRef<string | null>(null);
  useEffect(() => {
    const currentId = conversationState.conversationId;
    const previousId = previousConversationIdRef.current;

    // If we have a new conversation ID and a pending access point, track it
    if (currentId && currentId !== previousId && pendingAccessPointRef.current) {
      const { accessPoint, query } = pendingAccessPointRef.current;
      const isNew = previousId === null;

      // Track the conversation access point (non-blocking)
      trackConversationStart(accessPoint, query, isNew);
      trackedConversationIdRef.current = currentId;

      // Clear the pending access point
      pendingAccessPointRef.current = null;
    }

    previousConversationIdRef.current = currentId;
  }, [conversationState.conversationId, trackConversationStart]);

  // Generic interaction types that should NOT override an entry-point access point
  // (e.g., floating_button). Specific entry points like conversation_starter always override.
  const GENERIC_INTERACTION_TYPES: ReadonlySet<ConversationAccessPointType> = useMemo(
    () => new Set<ConversationAccessPointType>(['search_bar', 'quick_prompts', 'follow_up_question']),
    [],
  );

  // Callback to track access point - called by child components.
  // Access point is only recorded once per conversation; subsequent calls are no-ops.
  const handleTrackAccessPoint = useCallback(
    (accessPoint: ConversationAccessPointType, query: string) => {
      // Already tracked for this conversation — nothing to do
      if (conversationState.conversationId && trackedConversationIdRef.current === conversationState.conversationId) {
        return;
      }

      if (pendingAccessPointRef.current && GENERIC_INTERACTION_TYPES.has(accessPoint)) {
        // A generic interaction (typing, clicking prompt) shouldn't override
        // an entry-point access point (e.g., floating_button) — just update the query.
        pendingAccessPointRef.current.query = query;
      } else {
        pendingAccessPointRef.current = { accessPoint, query };
      }

      // If we already have a conversation ID, track immediately
      if (conversationState.conversationId) {
        const { accessPoint: ap, query: q } = pendingAccessPointRef.current;
        const isNew = isNewConversationRef.current;
        trackConversationStart(ap, q, isNew);
        trackedConversationIdRef.current = conversationState.conversationId;
        pendingAccessPointRef.current = null;
      }
    },
    [conversationState.conversationId, trackConversationStart, GENERIC_INTERACTION_TYPES],
  );

  // Get widget state and pending query
  const { isOpen } = widgetState;
  const { pendingQuery, pendingQueryId, clearPendingQuery } = useConciergePendingQuery();

  // Detect widget open via floating button (no pre-loaded query) and set floating_button
  // as the entry-point access point. PDP conversation starters and suggested pills set
  // pendingQuery/initialQuery before opening, so this only fires for plain icon clicks.
  // Skip when a conversationId or sharedConversationId is already present — that means
  // the conversation was loaded from URL or context, not initiated by clicking the icon.
  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (
      !wasOpen &&
      isOpen &&
      !pendingQuery &&
      !initialQuery &&
      !conversationState.conversationId &&
      !conversationState.sharedConversationId
    ) {
      pendingAccessPointRef.current = { accessPoint: 'floating_button', query: '' };
    }
  }, [isOpen, pendingQuery, initialQuery, conversationState.conversationId, conversationState.sharedConversationId]);

  // Ref to track the last processed query ID to prevent duplicate sends
  const lastProcessedQueryIdRef = useRef<string | null>(null);

  // Sync streaming / loading status and streaming text
  // When the AI SDK path is active (assistant message has parts), DON'T set
  // streamingAIResponse — ConciergeMessagePart handles progressive rendering.
  // Setting it would create a duplicate StreamingMessage overlay in MessageListV2.
  useEffect(() => {
    conversationState.setIsActivelyStreaming(chat.isStreaming || chat.isLoading);
    if (chat.isStreaming) {
      const lastMsg = chat.messages[chat.messages.length - 1];
      const hasActiveParts =
        lastMsg?.role === 'assistant' &&
        Array.isArray(lastMsg.parts) &&
        lastMsg.parts.some((p: any) => p.type !== 'text' || (p as any).text?.length > 0);
      if (!hasActiveParts) {
        const text =
          lastMsg?.role === 'assistant'
            ? lastMsg.parts
                .filter((p: any) => p.type === 'text' && 'text' in p)
                .map((p: any) => p.text)
                .join('')
            : '';
        conversationState.setStreamingAIResponse(text);
      }
    } else if (!chat.isLoading) {
      conversationState.clearStreamingState();
    }
  }, [chat.isStreaming, chat.isLoading, chat.messages]);

  // Auto-scroll behavior
  const { chatWrapperRef, messagesEndRef, shouldScrollOnNextMessageRef, scrollToBottom } = useAutoScroll(
    effectiveMessages.length,
    conversationState.isActivelyStreaming,
    conversationState.streamingAIResponse,
    effectiveMessages.length === 0,
  );

  // Expose performSearch / cancelStream for existing call sites
  const performSearch = useCallback(
    async (text: string, files?: Array<{ type: 'file'; mediaType: string; url: string }>) => {
      shouldScrollOnNextMessageRef.current = true;
      // A new turn starts clean: a stop from the previous turn must not
      // suppress a genuine error on this one.
      userStoppedRef.current = false;
      await chat.sendMessage(files?.length ? { text, files } : text);
    },
    [chat, shouldScrollOnNextMessageRef],
  );

  const cancelStream = useCallback(() => {
    userStoppedRef.current = true;
    chat.stop();
  }, [chat]);
  const resetChatMessages = useCallback(() => {
    (chat as any).setMessages?.([]);
  }, [chat]);

  // Keep the ref in sync so resetConversationState can use it
  cancelStreamRef.current = cancelStream;
  resetChatMessagesRef.current = resetChatMessages;

  // ── Agreement gate ─────────────────────────────────────────────────────
  // Matches ConciergeContainer behavior: intercept the first user send when
  // studio has an agreement configured and the shopper has not accepted yet.
  const agreementGate = useConciergeAgreementGate({
    appIdV2: appId,
    apiBaseUrl: conversationState.apiBaseUrl,
    messages: effectiveMessages,
    onShown: useCallback(() => {
      AnalyticsService.track(AnalyticsTrackingEvent.CONCIERGE_AGREEMENT_SHOWN, {
        appVersion: 'v2',
        ...(appId && { appId }),
      });
    }, [appId]),
    onAccepted: useCallback(() => {
      AnalyticsService.track(AnalyticsTrackingEvent.CONCIERGE_AGREEMENT_ACCEPTED, {
        appVersion: 'v2',
        ...(appId && { appId }),
      });
    }, [appId]),
  });

  const handleAgreementAccept = useCallback(() => {
    const pending = agreementGate.accept();
    if (pending) {
      void performSearch(pending.text, pending.files);
    }
  }, [agreementGate, performSearch]);

  // Expose gate reset to callbacks declared before the gate hook (new-conversation).
  useEffect(() => {
    agreementResetRef.current = agreementGate.reset;
    return () => {
      agreementResetRef.current = null;
    };
  }, [agreementGate.reset]);

  // Handle search submission
  // Attachments that went out with the most recent send — see the matching
  // comment in `ConciergeContainer`. `handleStreamError` restores only the TEXT
  // of a failed message, so without this the retry drops the images.
  const lastSubmittedFilesRef = useRef<Array<{ type: 'file'; mediaType: string; url: string }> | undefined>(undefined);

  const handleSearch = useCallback(
    async (searchTerm: string, files?: Array<{ type: 'file'; mediaType: string; url: string }>) => {
      lastSubmittedFilesRef.current = files;
      setSearchInputValue('');
      setStreamErrorMessage(null);
      if (agreementGate.interceptSend(searchTerm, files)) {
        return;
      }
      await performSearch(searchTerm, files);
    },
    [pageContext, performSearch, effectiveMessages.length, agreementGate],
  );

  const handleStreamErrorRetry = useCallback(() => {
    const text = searchInputValue.trim();
    const files = lastSubmittedFilesRef.current;
    setStreamErrorMessage(null);
    if (!text && !files?.length) return;
    void handleSearch(text, files);
  }, [searchInputValue, handleSearch]);

  // Keep a stable ref so the onConversationLoaded closure (not re-subscribed on every render)
  // can always invoke the latest handleSearch.
  const handleSearchRef = useRef(handleSearch);
  useEffect(() => {
    handleSearchRef.current = handleSearch;
  }, [handleSearch]);

  const handleStopWithRollback = useCallback(() => {
    void (async () => {
      const restoredPrompt = await chat.cancelLatestTurn();
      conversationState.setIsActivelyStreaming(false);
      conversationState.clearStreamingState();
      if (restoredPrompt) {
        setSearchInputValue(restoredPrompt);
      }
    })();
  }, [chat, conversationState]);

  // Handle update feedback
  const handleUpdateFeedback = (
    messageIndex: number,
    feedback: { type: 'like' | 'dislike'; timestamp: string } | null,
  ) => {
    // Update the feedback in the conversation history
    conversationState.setConversationHistory((prev) => {
      return prev.map((msg, idx) => {
        if (idx === messageIndex) {
          return {
            ...msg,
            feedback: feedback || undefined,
          };
        }
        return msg;
      });
    });
  };

  // Conversation loader
  const { isLoadingConversationQuery, markConversationStale } = useConversationLoader({
    conversationId: conversationState.conversationId,
    sharedConversationId: conversationState.sharedConversationId,
    isActivelyStreaming: conversationState.isActivelyStreaming,
    conversationHistoryLength: effectiveMessages.length,
    apiBaseUrl: conversationState.apiBaseUrl,
    onConversationLoaded: (messages, followUpQuestions, sharingInfo) => {
      conversationState.setConversationHistory(messages);
      conversationState.setFollowUpQuestions(followUpQuestions);

      // Seed useChat with the loaded history so follow-up sends don't erase old messages
      (chat as any).setMessages?.(messages);

      // Propagate sharing metadata into context state
      if (sharingInfo) {
        conversationState.setIsSharedView(sharingInfo.isSharedView ?? false);
        conversationState.setSharedConversationId(sharingInfo.sharedConversationId || null);
        conversationState.setSharedByUserName(sharingInfo.sharedByUserName);
        conversationState.setSharedAtMessageIndex(sharingInfo.sharedAtMessageIndex);
        conversationState.setSourceConversationId(sharingInfo.sourceConversationId);
        conversationState.setSourceCheckpointIndex(sharingInfo.sourceCheckpointIndex);
      } else {
        conversationState.setSharedConversationId(null);
        conversationState.setIsSharedView(false);
        conversationState.setSharedByUserName(undefined);
        conversationState.setSharedAtMessageIndex(undefined);
        conversationState.setSourceConversationId(undefined);
        conversationState.setSourceCheckpointIndex(undefined);
      }
    },
    onConversationNotFound: () => {
      const isInitialUrlCleanup =
        conversationState.conversationId === initialConversationIdRef.current &&
        conversationState.sharedConversationId === initialSharedConversationIdRef.current;
      if (!isInitialUrlCleanup) {
        toast.error('Conversation not found.');
      }
      conversationState.setConversationId(null);
      conversationState.setSharedConversationId(null);
      conversationState.setConversationPageContext(null);
      conversationState.setIsSharedView(false);
      conversationState.setSharedByUserName(undefined);
      conversationState.setSharedAtMessageIndex(undefined);
      conversationState.setSourceConversationId(undefined);
      conversationState.setSourceCheckpointIndex(undefined);
    },
  });

  // Plan 9 / D-85 — refetch conversation after voice call ends (D-66 inline voice segment).
  useEffect(() => {
    return conversationState.registerOnVoiceCallEnded(markConversationStale);
  }, [conversationState.registerOnVoiceCallEnded, markConversationStale]);

  // Sync conversationId with URL (disabled in widget)
  // useUrlSync(conversationState.conversationId);

  // Auto-send from URL params (disabled in widget)
  // useAutoSendFromUrl({
  //   isActivelyStreaming: conversationState.isActivelyStreaming,
  //   conversationLength: conversationState.conversationHistory.length,
  //   onSearch: handleSearch,
  // });

  // Handle pending queries from PDP conversation starters (Zustand-based - for backward compatibility)
  // This effect runs when: widget opens, or a new pending query is set
  useEffect(() => {
    // Skip if no pending query or widget not open
    if (!pendingQuery || !pendingQueryId || !isOpen) {
      return;
    }

    // Guard: Ensure performSearch is ready before processing
    if (!performSearch || typeof performSearch !== 'function') {
      return;
    }

    // Skip if we already processed this exact query (by ID)
    if (lastProcessedQueryIdRef.current === pendingQueryId) {
      return;
    }

    // Mark as processed immediately to prevent re-processing
    lastProcessedQueryIdRef.current = pendingQueryId;

    // Capture values before clearing
    const queryToSend = pendingQuery;

    // Clear the pending query immediately
    clearPendingQuery();

    const messageToSend = queryToSend;

    // Add a small delay to ensure the component is fully mounted and ready
    // This prevents crashes when the widget opens and immediately tries to process a query
    const timeoutId = setTimeout(() => {
      // Double-check that widget is still open and performSearch is still available
      if (!isOpen || !performSearch || typeof performSearch !== 'function') {
        // Reset the processed query ID so it can be retried later
        lastProcessedQueryIdRef.current = null;
        return;
      }

      // Send the message - performSearch handles adding user message and streaming response
      // Wrap in try-catch to prevent crashes if performSearch fails
      try {
        // Track conversation_starter access point (non-blocking)
        // This tracks PDP conversation starters (e.g., "Ask about this product" buttons)
        handleTrackAccessPoint('conversation_starter', queryToSend);
        // Honor the agreement gate for auto-sent PDP starters too
        if (agreementGate.interceptSend(messageToSend)) {
          return;
        }
        performSearch(messageToSend);
      } catch (error) {
        console.error('Error processing pending query:', error);
        toast.error('Failed to process search. Please try again.');
        // Reset the processed query ID so it can be retried
        lastProcessedQueryIdRef.current = null;
      }
    }, 100); // Small delay to ensure component is fully mounted

    // Cleanup timeout if component unmounts or dependencies change
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen, pendingQuery, pendingQueryId, performSearch, clearPendingQuery, handleTrackAccessPoint, agreementGate]);

  // Detect when the user completes inline OTP login (false → true isSignedIn transition).
  // Send a sign-in signal so the LLM can welcome the user by name (resolved server-side
  // from the now-valid auth token) and ask them to repeat their question.
  //
  // D-77 — Skip during an active voice call. The voice mid-call login path
  // (D-48) already retries the originally-blocked tool via `notifyLoginCompleted`
  // + `/voice/auth-promote`. Firing this text-mode auto-prompt in parallel
  // double-executes the user's intent and persists non-`voice-` ids into the
  // Mastra thread, which splits the single voice run into multiple
  // `<VoiceSegmentSeparatorCard>` rows in the text-chat replay.
  const prevIsSignedInRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    const wasSignedIn = prevIsSignedInRef.current;
    prevIsSignedInRef.current = conversationState.isSignedIn;

    if (wasSignedIn !== false || !conversationState.isSignedIn) return;
    if (conversationState.voiceCallOpen) return;

    // Bypass the agreement gate for the system-triggered sign-in signal.
    setTimeout(() => void performSearch('I just signed in.'), 300);
  }, [conversationState.isSignedIn, conversationState.voiceCallOpen, performSearch]);

  // Handle initial query from props (props-based - for checkout-shop-app and PDP conversation starters)
  // This avoids cross-bundle Zustand store issues by using props instead
  const lastProcessedInitialQueryRef = useRef<string | null>(null);
  const isProcessingInitialQueryRef = useRef(false);

  useEffect(() => {
    // Skip if no initialQuery or widget not open
    if (!initialQuery || !isOpen) {
      return;
    }

    // Guard: Ensure performSearch is ready before processing
    if (!performSearch || typeof performSearch !== 'function') {
      return;
    }

    // Skip if we already processed this exact query
    if (lastProcessedInitialQueryRef.current === initialQuery) {
      return;
    }

    // Skip if we're currently processing (timeout is pending)
    if (isProcessingInitialQueryRef.current) {
      return;
    }

    // Mark that we're starting to process (prevents duplicate timeouts)
    isProcessingInitialQueryRef.current = true;

    // Capture values before callback
    const queryToSend = initialQuery;
    const messageToSend = queryToSend;

    // Add a small delay to ensure the component is fully mounted and ready
    const timeoutId = setTimeout(() => {
      // Double-check that widget is still open and performSearch is still available
      if (!isOpen || !performSearch || typeof performSearch !== 'function') {
        // Reset so it can be retried later
        isProcessingInitialQueryRef.current = false;
        return;
      }

      // Send the message
      try {
        // Track conversation_starter access point for PDP pills
        handleTrackAccessPoint('conversation_starter', queryToSend);
        // Honor the agreement gate for auto-sent initial queries too
        if (agreementGate.interceptSend(messageToSend)) {
          lastProcessedInitialQueryRef.current = queryToSend;
          onInitialQueryProcessed?.();
          return;
        }
        performSearch(messageToSend);
        // Mark as successfully processed (prevents re-processing same query)
        lastProcessedInitialQueryRef.current = queryToSend;
        // Notify parent that the query has been processed
        onInitialQueryProcessed?.();
      } catch (error) {
        console.error('Error processing initial query:', error);
        toast.error('Failed to process search. Please try again.');
      } finally {
        isProcessingInitialQueryRef.current = false;
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      // Don't reset isProcessingInitialQueryRef here - let the effect re-run and decide
      // based on whether the query has actually been processed
      isProcessingInitialQueryRef.current = false;
    };
  }, [isOpen, initialQuery, performSearch, onInitialQueryProcessed, handleTrackAccessPoint, agreementGate]);

  // State for fetched pinned product
  // NOTE: These hooks MUST be called before any conditional returns to follow React's rules of hooks
  const [pinnedProduct, setPinnedProduct] = useState<SectionBasedProduct | null>(null);
  const [isPinnedProductLoading, setIsPinnedProductLoading] = useState(false);

  // Fetch pinned product when page context changes
  useEffect(() => {
    // Derive stacklineSku inside the effect to avoid stale closures
    const currentPageType = pageContext?.pageType;
    const currentStacklineSku = currentPageType === 'pdp' ? pageContext?.stacklineSku : undefined;

    // Clear previous product when context changes
    setPinnedProduct(null);
    setIsPinnedProductLoading(false);

    if (!currentStacklineSku) {
      return;
    }

    // Fetch product metadata
    let isCancelled = false;

    const fetchProduct = async () => {
      setIsPinnedProductLoading(true);
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const product = await productApi.fetchV2ByStacklineSku({
          stacklineSku: currentStacklineSku,
          headersToInject: headers,
          apiBaseUrl: conversationState.apiBaseUrl,
        });

        if (!isCancelled && product) {
          setPinnedProduct(product);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error fetching pinned product:', error);
        }
      } finally {
        if (!isCancelled) {
          setIsPinnedProductLoading(false);
        }
      }
    };

    fetchProduct();

    return () => {
      isCancelled = true;
    };
  }, [pageContext?.pageType, pageContext?.stacklineSku, conversationState.apiBaseUrl]);

  const filteredConversationHistory = chat.messages.length > 0 ? chat.messages : conversationState.conversationHistory;

  const filteredStreamingAIResponse = conversationState.streamingAIResponse;

  // Read panel state & color resolver directly (no WidgetWrapper = no edit icon)
  const panelState = useWidgetConfig<ConciergePanelWidgetState>('concierge-panel', {
    preset: 'vertical-expanded',
    mainBgColor: customColor('transparent'),
    pillLayout: 'vertical',
    pillShowIcon: false,
    pillBorderRadius: 100,
    pillBorderColor: customColor('#F3F3F3'),
    pillBorderWidth: 1,
    pillShadowEnabled: true,
    pillBgColor: customColor('#ffffff'),
    pillTextColor: customColor('#181d56'),
    pillShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.05)',
    pillContainerBgColor: customColor('transparent'),
    pillContainerBorderRadius: 12,
    inputBorderColor: customColor('#F3F3F3'),
    inputBorderWidth: 1,
    inputBorderRadius: 20,
    inputMinHeight: 116,
    inputShadowEnabled: false,
    inputShadow: '8px 8px 14px 0 rgba(0, 0, 0, 0.03)',
  });
  const resolveColor = useResolveColor();

  const mainBg = resolveColor(panelState.mainBgColor);
  const hasMainBg = mainBg && mainBg !== 'transparent' && mainBg !== '';

  // The card positions itself based on agreement.template; the host only
  // needs to provide a positioned ancestor (concierge-root has position: relative).
  const agreementOverlay =
    agreementGate.shouldShowCard && agreementGate.agreement ? (
      <ConciergeAgreementCard
        agreement={agreementGate.agreement}
        themeColors={{
          textPrimary: themeColors.textPrimary,
          primary: themeColors.primary,
          buttonText: themeColors.buttonText,
        }}
        pendingUserPrompt={agreementGate.pending?.text}
        onAgree={handleAgreementAccept}
      />
    ) : null;

  // D-87 — voice overlay is mounted inside `.concierge-body` (below the
  // header) instead of at the bubble container root. This keeps the bubble
  // header (BENNY title, X close, maximize, new-conversation) visible during
  // a call so the user can dismiss the bubble / open a fresh chat without
  // first having to hit End. The overlay uses `position: absolute; inset: 0`
  // scoped to `.concierge-body`, so it covers the chat messages + input area
  // exactly — same way the bubble shows them in text mode minus the bubble
  // chrome.
  const pdpStacklineSku =
    pageContext?.pageType === 'pdp' ? pageContext.stacklineSku : undefined;

  return (
    <>
      <Toaster />
      <div
        className="concierge-root"
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          maxHeight: '100%',
          overflow: 'hidden',
          backgroundColor: hasMainBg ? mainBg : undefined,
        }}
      >
        <ConciergeWidgetHeader
          showMaximize={showMaximize}
          isMaximized={isMaximized}
          onMaximize={handleMaximizeClick}
          onMinimize={onMinimize}
          onClose={widgetState.close}
          onHistoryClick={handleHistoryClick}
          onNewConversationClick={handleNewConversationClick}
          showNewConversation={true}
          newConversationIcon={<IconCompose size={16} />}
          title={title || studioAgentName || config?.conciergeAgentName}
          bottomContent={
            <PinnedProductContext
              pageContext={pageContext}
              product={pinnedProduct}
              isLoading={isPinnedProductLoading}
            />
          }
        />
        <div
          className="concierge-body"
          style={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DevOptionsPanel {...devOpts} />
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            <div style={{ display: 'flex', flex: 1, minWidth: 0 }}>
          <ConciergeChatAreaV2
            appName={config?.appName}
            conciergeName={studioAgentName || config?.conciergeAgentName}
            brandId={brandId}
            conversationId={conversationState.conversationId}
            isEmbedded={true}
            isOnChatWidget={true}
            apiBaseUrl={conversationState.apiBaseUrl}
            userFirstName={userFirstName}
            pageContext={pageContext}
            conversationHistory={filteredConversationHistory}
            followUpQuestions={conversationState.followUpQuestions}
            latestFollowUps={chat.latestFollowUps}
            isLoadingFollowUps={chat.isLoadingFollowUps}
            isActivelyStreaming={conversationState.isActivelyStreaming}
            transientStatus={chat.transientStatus}
            streamingAIResponse={filteredStreamingAIResponse}
            isLoadingConversationQuery={isLoadingConversationQuery}
            handleSearch={handleSearch}
            onRetryLastMessage={chat.retryLastMessage}
            streamErrorMessage={streamErrorMessage}
            onStreamErrorRetry={handleStreamErrorRetry}
            onUpdateFeedback={handleUpdateFeedback}
            onVariantSelect={onVariantSelect}
            onTrackAccessPoint={handleTrackAccessPoint}
            onStop={handleStopWithRollback}
            searchInputValue={searchInputValue}
            setSearchInputValue={setSearchInputValue}
            isSharedView={conversationState.isSharedView}
            sharedByUserName={conversationState.sharedByUserName}
            sourceConversationId={conversationState.sourceConversationId}
            sourceCheckpointIndex={conversationState.sourceCheckpointIndex}
            onSupportEventsReconnect={markConversationStale}
          />
            </div>
          </div>
          {conversationState.voiceCallOpen && conversationState.conversationId && (
            <VoiceCallOverlay
              open
              conversationId={conversationState.conversationId}
              pdpStacklineSku={pdpStacklineSku}
              onClose={handleVoiceCallClose}
              containerScoped
            />
          )}
        </div>
        {/* Full-panel overlay: covers the header + pinned bar + chat; the X is
            the way back (mounted at the root so it can sit over the header). */}
        <ProductDetailPanel variant="overlay" />
        {agreementOverlay}
      </div>
    </>
  );
};

export interface ConciergeWidgetChatProps {
  pageContext?: PageContext;
  widgetState: ConciergeWidgetState;
  showMaximize?: boolean;
  isMaximized?: boolean;
  onMaximize?: () => void;
  onMinimize?: () => void;
  /** Callback when a variant is selected in a FeaturedProductWidget - triggers navigation to that variant's PDP */
  onVariantSelect?: (stacklineSku: string) => void;
  title?: string;
  /** Initial query to auto-send when widget opens (passed via props to avoid cross-bundle Zustand issues) */
  initialQuery?: string;
  /** Stackline SKU associated with the initial query (for product context) */
  initialQueryStacklineSku?: string;
  /** Called after the initial query has been processed/sent */
  onInitialQueryProcessed?: () => void;
  /** Brand ID for fetching suggested queries. If not provided, will try to get from URL params or brand config */
  brandId?: number;
  /** Explicit override for the dedicated concierge page URL. If not provided, uses the
   *  server-computed `canonicalConciergeUrl` from the brand-app config (the brand's chosen
   *  serving surface — Plan 6), falling back to origin + /concierge. */
  conciergePageUrl?: string;
}

// ConciergeWidgetChat expects to be inside a QueryClientProvider (provided by ConciergeStandalone)
export const ConciergeWidgetChat: React.FC<ConciergeWidgetChatProps> = (props) => {
  return <ConciergeWidgetChatContent {...props} />;
};
